'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@quadminds/mindkit';
import type { FileItem } from '@quadminds/mindy-app/types/File/File.types';
import { Trash2, FolderInput, Power, X, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SelectionActionsProps {
    selectedItems: FileItem[];
    onClearSelection: () => void;
    onDeleteSelected: () => void;
    onMoveSelected: () => void;
    onToggleActiveSelected: (active: boolean) => void;
}

export function SelectionActions({
    selectedItems,
    onClearSelection,
    onDeleteSelected,
    onMoveSelected,
    onToggleActiveSelected,
}: SelectionActionsProps) {
    const { t } = useTranslation();
    // Verificar si todos los elementos seleccionados son archivos (no carpetas)
    const allFiles = selectedItems.every((item) => item.type === 'file');

    // Contar cuántos elementos están activos e inactivos
    const activeCount = selectedItems.filter((item) => item.active).length;
    const inactiveCount = selectedItems.length - activeCount;

    return (
        <div
            className="bg-primary/10 dark:bg-primary/50 border border-primary/20 dark:border-primary rounded-lg py-2 pl-4 pr-2 flex items-center justify-between"
            data-testid="selection-actions"
        >
            <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">
                    {t('knowledgeManager.infiniteOptions.selectedElements', { quantity: selectedItems.length })}
                </span>
                <Button variant="ghost" size="sm" onClick={onClearSelection} title="Cancelar selección">
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* For large screens - show buttons */}
            <div className="hidden lg:flex items-center space-x-2">
                {allFiles && (
                    <>
                        {inactiveCount > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onToggleActiveSelected(true)}
                                className="flex items-center gap-1"
                                data-testid="active-all"
                            >
                                <Power className="h-4 w-4" />
                                <span>{t('knowledgeManager.infiniteOptions.active')}</span>
                            </Button>
                        )}

                        {activeCount > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onToggleActiveSelected(false)}
                                className="flex items-center gap-1"
                                data-testid="not-active-all"
                            >
                                <Power className="h-4 w-4" />
                                <span>{t('knowledgeManager.infiniteOptions.notActived')}</span>
                            </Button>
                        )}

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onMoveSelected}
                            className="flex items-center gap-1"
                        >
                            <FolderInput className="h-4 w-4" />
                            <span>{t('knowledgeManager.infiniteOptions.moveTo')}</span>
                        </Button>
                    </>
                )}

                <Button
                    variant="destructive"
                    size="sm"
                    onClick={onDeleteSelected}
                    className="flex items-center gap-1"
                    data-testid="delete-all"
                >
                    <Trash2 className="h-4 w-4" />
                    <span>{t('knowledgeManager.infiniteOptions.delete')}</span>
                </Button>
            </div>

            {/* For small screens - show dropdown menu */}
            <div className="lg:hidden">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                            <div className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                <span>{t('knowledgeManager.infiniteOptions.actions')}</span>
                            </div>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {allFiles && (
                            <>
                                {inactiveCount > 0 && (
                                    <DropdownMenuItem onClick={() => onToggleActiveSelected(true)}>
                                        <div className="flex items-center gap-2">
                                            <Power className="h-4 w-4" />
                                            <span>{t('knowledgeManager.infiniteOptions.active')}</span>
                                        </div>
                                    </DropdownMenuItem>
                                )}

                                {activeCount > 0 && (
                                    <DropdownMenuItem onClick={() => onToggleActiveSelected(false)}>
                                        <div className="flex items-center gap-2">
                                            <Power className="h-4 w-4" />
                                            <span>{t('knowledgeManager.infiniteOptions.notActived')}</span>
                                        </div>
                                    </DropdownMenuItem>
                                )}

                                <DropdownMenuItem onClick={onMoveSelected}>
                                    <div className="flex items-center gap-2">
                                        <FolderInput className="h-4 w-4" />
                                        <span>{t('knowledgeManager.infiniteOptions.moveTo')}</span>
                                    </div>
                                </DropdownMenuItem>
                            </>
                        )}

                        <DropdownMenuItem onClick={onDeleteSelected} className="text-destructive">
                            <div className="flex items-center gap-2">
                                <Trash2 className="h-4 w-4" />
                                <span>{t('knowledgeManager.infiniteOptions.delete')}</span>
                            </div>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

export default SelectionActions;
